/**
 * BuildingResolver — resolves a user's raw GPS position to the nearest
 * named physical feature (building, amenity, shop, tourism POI) via
 * OpenStreetMap's Overpass API.
 *
 * Why this exists: GPS in urban environments has ±5-15m accuracy, and
 * the Guide LLM happily says things like "the building on your right"
 * which is actually 15m off because the raw lat/lon is not snapped to
 * any real feature. By resolving to a concrete named entity with a
 * known distance, we give Guide a precise anchor instead of a fuzzy
 * coordinate.
 *
 * Used by GpsTrackerApp._sendAndListen() in walking/scooter mode. In
 * car mode the resolver is skipped because landscape-scale narration
 * doesn't care about building-level precision.
 *
 * Overpass API notes:
 * - Public mirror at overpass-api.de, no key required, rate-limited
 *   ~1 req / 10s for polite usage.
 * - We debounce aggressively (cache keyed by rounded coordinates to
 *   avoid repeated queries when standing still).
 * - Timeout capped at 8 seconds — if the API is slow we return null
 *   and Guide falls back to the raw coordinate behavior.
 */
/**
 * Default inspiration radius per transport mode (meters).
 *
 * This is NOT a "what's physically next to me" radius — it's a
 * conceptual radius that tells the AI how far to think:
 *   sitting  = inspiration mode, worldwide
 *   walking  = neighbourhood, what's around the corner
 *   bike     = district/town scale
 *   scooter  = city scale, day-trip destinations
 *   car      = regional, 50km reach
 *
 * For large radii (>2km), the OSM Overpass query is skipped and only
 * the radius value is sent as a prompt hint to the AI. Overpass is
 * only used for small radii where concrete nearby objects are useful.
 */
export const RADIUS_PRESETS = {
    sitting: 100000, // 100km — inspiration mode
    walking: 200,
    bike: 2000,
    scooter: 5000,
    car: 50000, // 50km
};
export class BuildingResolver {
    constructor() {
        this._lastRequestAt = 0;
        this._cache = new Map();
        this._cacheAll = new Map();
    }
    /**
     * Find the nearest named feature to the given coordinate.
     * Returns null on network error, timeout, rate-limit, or if no
     * named features exist within `radiusM`.
     */
    async findNearest(lat, lon, radiusM = BuildingResolver.DEFAULT_RADIUS_M) {
        const cacheKey = this._cacheKey(lat, lon, radiusM);
        const cached = this._cache.get(cacheKey);
        if (cached && Date.now() - cached.at < BuildingResolver.CACHE_TTL_MS) {
            return cached.result;
        }
        // Rate limit: drop the request entirely rather than queueing,
        // because the caller (_sendAndListen) runs on a fixed interval
        // and a queued stale result is useless by the time it arrives.
        const sinceLast = Date.now() - this._lastRequestAt;
        if (sinceLast < BuildingResolver.MIN_REQUEST_INTERVAL_MS) {
            return null;
        }
        this._lastRequestAt = Date.now();
        const query = this._buildOverpassQuery(lat, lon, radiusM);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), BuildingResolver.REQUEST_TIMEOUT_MS);
        try {
            const response = await fetch(BuildingResolver.OVERPASS_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: `data=${encodeURIComponent(query)}`,
                signal: controller.signal,
            });
            window.clearTimeout(timeoutId);
            if (!response.ok) {
                this._cache.set(cacheKey, { at: Date.now(), result: null });
                return null;
            }
            const data = (await response.json());
            const nearest = this._pickNearest(lat, lon, data);
            this._cache.set(cacheKey, { at: Date.now(), result: nearest });
            return nearest;
        }
        catch {
            window.clearTimeout(timeoutId);
            return null;
        }
    }
    /**
     * Find ALL named features within `radiusM`, sorted by distance.
     * Returns an empty array on error or when nothing is nearby.
     * Results are capped at MAX_NEARBY to keep IACP messages compact.
     */
    async findAll(lat, lon, radiusM) {
        const cacheKey = `all:${this._cacheKey(lat, lon, radiusM)}`;
        const cached = this._cacheAll.get(cacheKey);
        if (cached && Date.now() - cached.at < BuildingResolver.CACHE_TTL_MS) {
            return cached.result;
        }
        const sinceLast = Date.now() - this._lastRequestAt;
        if (sinceLast < BuildingResolver.MIN_REQUEST_INTERVAL_MS) {
            return [];
        }
        this._lastRequestAt = Date.now();
        const query = this._buildOverpassQuery(lat, lon, radiusM);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), BuildingResolver.REQUEST_TIMEOUT_MS);
        try {
            const response = await fetch(BuildingResolver.OVERPASS_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: `data=${encodeURIComponent(query)}`,
                signal: controller.signal,
            });
            window.clearTimeout(timeoutId);
            if (!response.ok) {
                this._cacheAll.set(cacheKey, { at: Date.now(), result: [] });
                return [];
            }
            const data = (await response.json());
            const all = this._parseAll(lat, lon, data);
            this._cacheAll.set(cacheKey, { at: Date.now(), result: all });
            return all;
        }
        catch {
            window.clearTimeout(timeoutId);
            return [];
        }
    }
    /**
     * Format a findAll() result into a compact string for IACP messages.
     * Example: "Café Mozart (cafe, 15m) | Stadtpark (park, 45m) | Dom (church, 120m)"
     */
    static formatForMessage(items) {
        return items
            .map((b) => `${b.name} (${b.kind}, ${b.distanceM}m)`)
            .join(' | ');
    }
    _parseAll(lat, lon, data) {
        if (!data.elements || data.elements.length === 0)
            return [];
        const seen = new Set();
        const result = [];
        for (const el of data.elements) {
            const featureLat = el.type === 'way' ? el.center?.lat : el.lat;
            const featureLon = el.type === 'way' ? el.center?.lon : el.lon;
            if (typeof featureLat !== 'number' || typeof featureLon !== 'number')
                continue;
            const name = el.tags?.name;
            if (!name || seen.has(name))
                continue;
            seen.add(name);
            const distance = BuildingResolver._haversineMeters(lat, lon, featureLat, featureLon);
            const kind = this._classify(el.tags ?? {});
            result.push({ name, kind, distanceM: Math.round(distance), lat: featureLat, lon: featureLon });
        }
        result.sort((a, b) => a.distanceM - b.distanceM);
        return result.slice(0, BuildingResolver.MAX_NEARBY);
    }
    _cacheKey(lat, lon, radiusM) {
        const d = BuildingResolver.CACHE_KEY_DECIMALS;
        return `${lat.toFixed(d)},${lon.toFixed(d)},${radiusM}`;
    }
    _buildOverpassQuery(lat, lon, radiusM) {
        // Named buildings (ways), plus point POIs with a name tag in a
        // handful of interesting categories. `out center` gives ways a
        // centroid so we can compute a single distance per feature.
        return `[out:json][timeout:8];
(
  way(around:${radiusM},${lat},${lon})[building][name];
  node(around:${radiusM},${lat},${lon})[amenity][name];
  node(around:${radiusM},${lat},${lon})[shop][name];
  node(around:${radiusM},${lat},${lon})[tourism][name];
  node(around:${radiusM},${lat},${lon})[historic][name];
);
out center tags;`;
    }
    _pickNearest(lat, lon, data) {
        if (!data.elements || data.elements.length === 0)
            return null;
        let best = null;
        for (const el of data.elements) {
            const featureLat = el.type === 'way' ? el.center?.lat : el.lat;
            const featureLon = el.type === 'way' ? el.center?.lon : el.lon;
            if (typeof featureLat !== 'number' || typeof featureLon !== 'number')
                continue;
            const name = el.tags?.name;
            if (!name)
                continue;
            const distance = BuildingResolver._haversineMeters(lat, lon, featureLat, featureLon);
            const kind = this._classify(el.tags ?? {});
            if (!best || distance < best.distanceM) {
                best = { name, kind, distanceM: Math.round(distance), lat: featureLat, lon: featureLon };
            }
        }
        return best;
    }
    _classify(tags) {
        if (tags.building && tags.building !== 'yes')
            return `building:${tags.building}`;
        if (tags.building)
            return 'building';
        if (tags.amenity)
            return `amenity:${tags.amenity}`;
        if (tags.shop)
            return `shop:${tags.shop}`;
        if (tags.tourism)
            return `tourism:${tags.tourism}`;
        if (tags.historic)
            return `historic:${tags.historic}`;
        return 'feature';
    }
    /** Great-circle distance in meters between two lat/lon points */
    static _haversineMeters(lat1, lon1, lat2, lon2) {
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
}
BuildingResolver.OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
// 80m default catches larger building polygons whose centroid
// sits past the older 40m radius (e.g. a train station hall
// whose centroid is 50-60m from the forecourt where the user
// typically stands). Still tight enough to be "the building
// you're at", not "across the square".
BuildingResolver.DEFAULT_RADIUS_M = 80;
BuildingResolver.REQUEST_TIMEOUT_MS = 8000;
BuildingResolver.MIN_REQUEST_INTERVAL_MS = 10000;
BuildingResolver.CACHE_KEY_DECIMALS = 4;
/** Max objects to return from findAll() to keep message size sane */
BuildingResolver.MAX_NEARBY = 15;
BuildingResolver.CACHE_TTL_MS = 5 * 60 * 1000;
//# sourceMappingURL=BuildingResolver.js.map